/**
 * "Update permissions" (Story 19, task 80): the consent link for a Client
 * whose consent predates permissions the app now declares. Same shape as
 * the invite — URL and QR, shown once — with the missing permissions named
 * so the Client admin knows what they are granting.
 */

import { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';

import type { ReconsentResult } from '@bluesphere/api-client';
import { PillButton } from '@bluesphere/ui-kit';

import { ModalShell } from '@bluesphere/mdr-alert-panel';

export function ReconsentModal({
  displayName,
  result,
  onClose,
}: {
  displayName: string;
  result: ReconsentResult;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const url = result.consent_url;

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard API can fail in non-secure contexts; do nothing */
    }
  }

  return (
    <ModalShell onClose={onClose} width="max-w-xl">
      <div className="flex items-start justify-between gap-4 border-b border-bluesphere-border px-6 py-5">
        <div>
          <h2 className="text-lg font-semibold text-bluesphere-textOnLight">
            Update permissions for <span className="font-mono">{displayName}</span>
          </h2>
          <p className="mt-1 text-sm text-bluesphere-textSecondary">
            The Client's admin opens this link, signs in to their Microsoft tenant and re-consents.
            Ingestion is unaffected; the connection date does not move.{' '}
            <strong className="font-semibold">This is the only time we display the link.</strong>
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="px-2 text-2xl leading-none text-bluesphere-muted hover:text-bluesphere-textOnLight"
          aria-label="Close"
        >
          ×
        </button>
      </div>

      <div className="space-y-4 px-6 py-5">
        <div className="flex items-center justify-center rounded-input border border-bluesphere-border bg-white p-4">
          <QRCodeSVG value={url} size={220} level="M" includeMargin />
        </div>
        <div>
          <div className="mb-1 text-xs uppercase tracking-wider text-bluesphere-textSecondary">
            Consent URL
          </div>
          <div className="flex items-stretch gap-2">
            <input
              readOnly
              value={url}
              onFocus={(e) => e.currentTarget.select()}
              className="flex-1 rounded-input border border-bluesphere-borderMuted px-3 py-2 font-mono text-xs text-bluesphere-textOnLight"
            />
            <PillButton size="sm" onClick={copy}>
              {copied ? 'Copied' : 'Copy'}
            </PillButton>
          </div>
        </div>
        <div className="rounded-input border border-bluesphere-border bg-bluesphere-surfaceMuted px-3 py-2 text-xs text-bluesphere-textSecondary">
          <strong>Permissions this consent adds:</strong>{' '}
          {result.missing_scopes.length > 0 ? (
            <span className="font-mono">{result.missing_scopes.join(', ')}</span>
          ) : (
            'none outstanding'
          )}
          . The admin needs Global Administrator or Privileged Role Administrator in their tenant.
          Until they accept, the response actions that need these stay unavailable on this Client's
          alerts, with that reason shown.
        </div>
      </div>

      <div className="flex items-center justify-end border-t border-bluesphere-border px-6 py-3">
        <PillButton variant="ghost" size="sm" onClick={onClose}>
          Done
        </PillButton>
      </div>
    </ModalShell>
  );
}
